import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { ThemeProvider, useTheme } from '../context/ThemeContext'
import { gradients } from '../theme'
import { useLang } from '../context/LangContext'
import { supabase } from '../supabaseClient'
import qaraaLogo from '../images/qaraalogo.webp'
import qaraaCoffee from '../images/qaraacoffee.webp'

// Premium components
import DynamicHeader from '../components/DynamicHeader'
import FloatingNav from '../components/FloatingNav'
import InteractiveCard from '../components/InteractiveCard'
import DigitalCard from '../components/DigitalCard'
import LocationsView from '../components/LocationsView'
import ChatGuideView from '../components/ChatGuideView'
import Profile from './Profile'
import HistorySection from './HistorySection'

function DashboardContent() {
    usePageTitle('')
    const navigate = useNavigate()
    const { theme, isDark } = useTheme()
    const { t, setLang, lang } = useLang()

    const [customer, setCustomer] = useState(null)
    const [activeTab, setActiveTab] = useState('home')
    const [showCoffeeAlert, setShowCoffeeAlert] = useState(false)

    useEffect(() => {
        // Усиленный скролл вверх при монтировании
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0

        const timer = setTimeout(() => {
            window.scrollTo(0, 0)
        }, 100)

        return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
        const loadCustomer = async () => {
            try {
                const raw = localStorage.getItem('qaraa_customer')
                if (raw) {
                    const localData = JSON.parse(raw)

                    // Сначала показываем локальные данные для быстрой загрузки
                    setCustomer(localData)

                    // Затем синхронизируем с базой данных для получения актуальных бонусов
                    const { data: freshData, error } = await supabase
                        .from('customers')
                        .select('*')
                        .eq('id', localData.id)
                        .maybeSingle()

                    if (!error && freshData) {
                        // Обновляем состояние и localStorage актуальными данными
                        setCustomer(freshData)
                        localStorage.setItem('qaraa_customer', JSON.stringify(freshData))
                    }
                } else {
                    navigate('/login')
                }
            } catch (e) {
                console.error('Error loading customer:', e)
                navigate('/login')
            }
        }

        loadCustomer()

        // Poll for updates every 5 seconds for real-time data
        const interval = setInterval(loadCustomer, 5000)
        return () => clearInterval(interval)
    }, [navigate])

    // Scroll to top when changing tabs and update tracking
    useEffect(() => {
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0

        // Update global variable for UserTracker
        const tabNames = {
            'home': 'Главная (App)',
            'qr': 'QR Карта',
            'locations': 'Локации',
            'chat': 'Чат Guide',
            'profile': 'Профиль'
        }
        window.qaraaCurrentPage = tabNames[activeTab] || `Dashboard (${activeTab})`

        // Force immediate update if UserTracker is listening? 
        // UserTracker heartbeat is 15s. We can manually trigger an update here too for instant feedback.
        if (customer?.id) {
            supabase.from('customers').update({
                current_page: window.qaraaCurrentPage,
                last_active: new Date(),
                is_online: true
            }).eq('id', customer.id).then(() => { })
        }

    }, [activeTab, customer?.id])

    if (!customer) {
        return null
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'home':
                return (
                    <div style={{
                        padding: '20px',
                        paddingBottom: '100px',
                        background: '#F7F6F2',
                        minHeight: 'calc(100vh - 200px)'
                    }}>
                        {/* Section Title */}
                        <div style={{
                            marginBottom: '20px',
                            paddingLeft: '4px'
                        }}>
                            <h2 style={{
                                fontSize: '22px',
                                fontWeight: '700',
                                color: '#111',
                                margin: '0 0 4px 0',
                                letterSpacing: '-0.5px'
                            }}>
                                {lang === 'kk' ? 'Сервистер' : lang === 'en' ? 'Services' : 'Сервисы'}
                            </h2>
                            <p style={{
                                fontSize: '14px',
                                color: '#9A9890',
                                margin: 0,
                                fontWeight: '500'
                            }}>
                                {lang === 'kk' ? 'Қолжетімді сервистер' : lang === 'en' ? 'Available services' : 'Доступные сервисы'}
                            </p>
                        </div>

                        {/* Cards List */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            {/* Qaraa Store Card */}
                            <div
                                onClick={() => navigate('/storedashboard')}
                                style={{
                                    background: '#FFFFFF',
                                    borderRadius: '20px',
                                    padding: '24px',
                                    cursor: 'pointer',
                                    border: '0.5px solid #EAEAE4',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                    WebkitTapHighlightColor: 'transparent',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                                onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                {/* Background accent */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: '120px',
                                    height: '120px',
                                    background: 'radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 70%)',
                                    pointerEvents: 'none'
                                }} />

                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        marginBottom: '16px'
                                    }}>
                                        <div style={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <img
                                                src={qaraaLogo}
                                                alt="qaraa store"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{
                                                fontSize: '20px',
                                                fontWeight: '700',
                                                color: '#111',
                                                margin: '0 0 4px 0',
                                                letterSpacing: '-0.3px'
                                            }}>
                                                qaraa.asia
                                            </h3>
                                            <p style={{
                                                fontSize: '14px',
                                                color: '#9A9890',
                                                margin: 0,
                                                fontWeight: '500'
                                            }}>
                                                {lang === 'kk' ? 'Онлайн дүкен' : lang === 'en' ? 'Online Store' : 'Онлайн магазин'}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#f59e0b',
                                            fontWeight: '600'
                                        }}>
                                            {lang === 'kk' ? 'Дүкенге өтіңіз' : lang === 'en' ? 'Go to store' : 'Перейти в магазин'}
                                        </div>
                                        <div style={{
                                            fontSize: '20px',
                                            color: '#f59e0b'
                                        }}>→</div>
                                    </div>
                                </div>
                            </div>

                            {/* Qaraa Coffee Card */}
                            <div
                                onClick={() => setShowCoffeeAlert(true)}
                                style={{
                                    background: '#FFFFFF',
                                    borderRadius: '20px',
                                    padding: '24px',
                                    cursor: 'pointer',
                                    border: '0.5px solid #EAEAE4',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                    WebkitTapHighlightColor: 'transparent',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                                onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                {/* Background accent */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: '120px',
                                    height: '120px',
                                    background: 'radial-gradient(circle, rgba(90, 200, 250, 0.12) 0%, transparent 70%)',
                                    pointerEvents: 'none'
                                }} />

                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        marginBottom: '16px'
                                    }}>
                                        <div style={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <img
                                                src={qaraaCoffee}
                                                alt="qaraa coffee"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{
                                                fontSize: '20px',
                                                fontWeight: '700',
                                                color: '#111',
                                                margin: '0 0 4px 0',
                                                letterSpacing: '-0.3px'
                                            }}>
                                                qaraa.coffee
                                            </h3>
                                            <p style={{
                                                fontSize: '14px',
                                                color: '#9A9890',
                                                margin: 0,
                                                fontWeight: '500'
                                            }}>
                                                {lang === 'kk' ? 'Кофе үйі' : lang === 'en' ? 'Coffee House' : 'Кофейня'}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#007AFF',
                                            fontWeight: '600'
                                        }}>
                                            {lang === 'kk' ? 'Кофе үйіне өтіңіз' : lang === 'en' ? 'Go to coffee house' : 'Перейти в кофейню'}
                                        </div>
                                        <div style={{
                                            fontSize: '20px',
                                            color: '#007AFF'
                                        }}>→</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )

            case 'qr':
                return customer ? (
                    <DigitalCard
                        customerId={customer.id}
                        cardNumber={customer.card_number}
                        bonusBalance={customer.bonus_balance || 0}
                        userName={customer.first_name || t('home.user')}
                    />
                ) : null

            case 'locations':
                return <LocationsView />

            case 'chat':
                return <ChatGuideView />

            case 'history':
                return customer ? <HistorySection customer={customer} /> : null

            case 'profile':
                return (
                    <Profile
                        customer={customer}
                        onLogout={async () => {
                            try {
                                if (customer?.id) {
                                    await supabase
                                        .from('customers')
                                        .update({ is_online: false, last_active: new Date().toISOString() })
                                        .eq('id', customer.id)
                                }
                                localStorage.removeItem('qaraa_customer')
                            } catch (_) { }
                            // Force reload to clear App state immediately
                            window.location.href = '/'
                        }}
                        hideHeader={true}
                    />
                )

            default:
                return null
        }
    }

    return (
        <>
            <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float {
            0%, 100% { transform: translateY(0) translateX(0); }
            50% { transform: translateY(-20px) translateX(10px); }
        }
        body { background: #FAFAF8; }
      `}</style>

            <div style={{
                minHeight: '100vh',
                background: '#FAFAF8',
                paddingBottom: 'env(safe-area-inset-bottom)'
            }}>
                <DynamicHeader
                    userName={customer?.first_name || t('home.user')}
                    avatarUrl={customer?.avatar_url}
                    bonusBalance={customer?.bonus_balance || 0}
                    onAvatarClick={() => setActiveTab('profile')}
                />

                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                    {renderTabContent()}
                </div>

                <FloatingNav
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />
            </div>

            {/* Premium Coffee Alert Modal */}
            {showCoffeeAlert && (
                <div
                    onClick={() => setShowCoffeeAlert(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2000,
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(15px)',
                        WebkitBackdropFilter: 'blur(15px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px',
                        animation: 'fadeIn 0.3s ease'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '28px',
                            width: '100%',
                            maxWidth: '340px',
                            padding: '32px 24px',
                            textAlign: 'center',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                            animation: 'slideUpModal 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        <div style={{
                            marginBottom: '20px',
                            display: 'flex',
                            justifyContent: 'center',
                            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))'
                        }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h3 style={{
                            fontSize: '22px',
                            fontWeight: '800',
                            color: '#1a1a1a',
                            marginBottom: '12px',
                            letterSpacing: '-0.5px'
                        }}>
                            {t('home.coffee.title')}
                        </h3>
                        <p style={{
                            fontSize: '16px',
                            color: '#6B7280',
                            lineHeight: 1.5,
                            marginBottom: '28px',
                            fontWeight: '500'
                        }}>
                            {lang === 'kk' ? 'Қазіргі уақытта кіру уақытша жабық' : lang === 'en' ? 'Currently, access is temporarily closed' : 'В данный момент вход временно закрыт'}
                        </p>
                        <button
                            onClick={() => {
                                setShowCoffeeAlert(false)
                            }}
                            style={{
                                width: '100%',
                                height: '50px',
                                background: '#FFFFFF',
                                color: '#111',
                                border: '1.5px solid #111',
                                borderRadius: '100px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                letterSpacing: '-0.2px',
                                fontFamily: 'inherit',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.01)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            {lang === 'kk' ? 'Түсінікті' : lang === 'en' ? 'Got it' : 'Понятно'}
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUpModal {
                    from { opacity: 0; transform: translateY(40px) scale(0.9); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </>
    )
}

export default function DashboardMobile() {
    return (
        <ThemeProvider>
            <DashboardContent />
        </ThemeProvider>
    )
}
